import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import { IRoom, roomSchema } from '../Interfaces/IRoom';

const ROOM_QUERY_KEY = ['room'];

export const useGetRooms = ({ includeDeleted = false }: { includeDeleted?: boolean } = {}) => {
    const { data: roomArray, isLoading: isLoadingRoomArray } = useQuery<IRoom[]>({
        refetchOnWindowFocus: false,
        queryKey: [...ROOM_QUERY_KEY, includeDeleted],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: `/rooms/${includeDeleted ? '?include_deleted=true' : ''}`,
            });
            return roomSchema.array().parse(data);
        },
        initialData: [],
    });

    return { roomArray: roomArray as IRoom[], isLoadingRoomArray };
};

export const useCreateRoom = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createRoom, isPending: isCreatingRoom } = useMutation<IRoom, Error, Omit<IRoom, 'id'>>({
        mutationFn: (newRoom) =>
            customFetcher<IRoom>({
                endpoint: '/rooms/',
                method: 'POST',
                body: newRoom,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ROOM_QUERY_KEY });
        },
    });

    return { createRoom, isCreatingRoom };
};

export const useUpdateRoom = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateRoom, isPending: isUpdatingRoom } = useMutation<IRoom, Error, IRoom>({
        mutationFn: (updatedRoom) =>
            customFetcher<IRoom>({
                endpoint: `/rooms/${updatedRoom.id}/`,
                method: 'PUT',
                body: updatedRoom,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ROOM_QUERY_KEY });
        },
    });

    return { updateRoom, isUpdatingRoom };
};

export const useDeleteRoom = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteRoom, isPending: isDeletingRoom } = useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await customFetcher({
                endpoint: `/rooms/${id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ROOM_QUERY_KEY });
        },
    });

    return { deleteRoom, isDeletingRoom };
};
